import React, { useEffect } from 'react';
import { getChildComponents } from '../../../../lib/utils/aem/getChildComponents';
import { classes } from '../../../../lib/utils/cssUtils';
import styles from './Tabs.module.scss';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import useActiveTab from '../../../../lib/hooks/useActiveTab';
import useMobileExpanded from '../../../../lib/hooks/useMobileExpanded';
import { tabsIds } from '../../../../lib/tests/testIds';
import { withInternalComponents } from '../../../../lib/context/InternalComponentContext';
import { TabsProps } from './Tabs.types';
import { isNil } from '../../../../lib/utils/isNil';

const Tabs = (props: TabsProps) => {
  const {
    cqItems,
    activeItem,
    cqItemsOrder,
    activeIndexFromAuthorPanel,
    isInEditor,
    baseCssClass,
    accessibilityLabel,
    items,
    elementsClasses,
    id,
    extraAttributes,
    updateHash,
    customPanelTitles,
  } = props;
  const [activeTab, setActiveTab] = useActiveTab(cqItems, activeItem ?? cqItemsOrder[0], updateHash);
  const [isMobileExpanded, setIsMobileExpanded, toggleMobileState] = useMobileExpanded(false);

  const currentItemIndex = cqItemsOrder.indexOf(activeTab);
  const childComponents = getChildComponents(props);

  const handleKeydownClick = (event: React.KeyboardEvent<HTMLLIElement>, item: string) => {
    if (event.key === 'Enter') {
      setActiveTab(item);
    }
  };

  useEffect(() => {
    if (!isNil(activeIndexFromAuthorPanel) && activeIndexFromAuthorPanel !== currentItemIndex) {
      setActiveTab(cqItemsOrder[activeIndexFromAuthorPanel]);
    }
  }, [activeIndexFromAuthorPanel]);

  useEffect(() => {
    setIsMobileExpanded(false);
  }, [activeTab]);

  if (!cqItems || !cqItemsOrder) {
    return null;
  }

  return (
    <div {...extraAttributes} className={classes('aem-container', baseCssClass)}>
      <div
        className={classes(styles.tabsNavigationContainer, elementsClasses?.tabNavigation, {
          [styles.tabNavigationMobileExpanded]: isMobileExpanded,
        })}
        data-testid={tabsIds.tabsNavigationContainer}
      >
        <button className={styles.tabsNavigationMobileHeader} onClick={toggleMobileState}>
          <span>{cqItems[activeTab]?.['cq:panelTitle']}</span>
          <LeftArrow className={styles.mobileArrow} />
        </button>
        <ol
          role="tablist"
          className={classes(`${baseCssClass}__tablist`, styles.tabsNavigation)}
          aria-label={accessibilityLabel}
          aria-multiselectable="false"
        >
          {cqItemsOrder.map((item: string) => {
            const tab = cqItems[item];
            const isActive = item === activeTab;
            const tabId = `${id}-tab-${item}`;
            const panelTitle = tab['cq:panelTitle'];

            return (
              <li
                role="tab"
                aria-selected={isActive}
                aria-controls={`${id}-${item}`}
                key={tabId}
                id={tabId}
                onClick={() => setActiveTab(item)}
                className={classes(`${baseCssClass}__tab`, styles.tabTitle, {
                  [` ${baseCssClass}__tab--active`]: isActive,
                  [styles.tabActive]: isActive,
                  [elementsClasses?.activeTab ?? '']: isActive,
                })}
                data-cmp-hook-tabs="tab"
                onKeyDown={event => handleKeydownClick(event, item)}
                tabIndex={0}
              >
                {customPanelTitles ? customPanelTitles[panelTitle] ?? '' : panelTitle}
              </li>
            );
          })}
        </ol>
      </div>
      {isInEditor ? (
        <>
          {childComponents.map((item, index) => {
            const isVisible = index === currentItemIndex;
            const itemKey = cqItemsOrder[index];

            return (
              <div
                key={`tab-content-${item?.key}`}
                className={classes(
                  `${baseCssClass}__author-tab-content`,
                  styles.tabContainer,
                  elementsClasses?.tabContainer,
                  {
                    [styles.hiddenTabContainer]: !isVisible,
                  },
                )}
              >
                {items?.[itemKey]?.top}
                {item}
                {items?.[itemKey]?.bottom}
              </div>
            );
          })}
        </>
      ) : (
        <div
          role="tabpanel"
          id={`${id}-${activeTab}`}
          aria-labelledby={`${id}-tab-${activeTab}`}
          className={classes(styles.tabContainer, elementsClasses?.tabContainer)}
        >
          {items?.[activeTab]?.top}
          {childComponents[currentItemIndex]}
          {items?.[activeTab]?.bottom}
        </div>
      )}
    </div>
  );
};

export default withInternalComponents(Tabs);
